import { useEffect, useState } from "react";
import {
  Button,
  MenuItem,
  CircularProgress,
  Grid,
  InputLabel,
  FormControl,
  Box,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import Select from "@mui/material/Select";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { put, post, get } from "src/utils/networkUtils";
import GameTagSearch from "../components/stage-admin/GameTagSearch";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import GameProviderMultilingualInputs from "src/components/game-provider-configs/GameProviderMultilingualInputs";
import GameProviderImages from "src/components/game-provider-configs/GameProviderImages";

const dateFormat = "YYYY-MM-DD HH:mm:ss";
const langs = [
  { key: "en", label: "English" },
  { key: "fr", label: "French" },
];

// TabPanel component for language tabs
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`language-tabpanel-${index}`}
      aria-labelledby={`language-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function GameProviderEditPage(props) {
  const { id } = useParams();
  const { currentGameProvider } = useSelector((state) => state.gameProvider);

  const [gameProvider, setGameProvider] = useState(
    currentGameProvider || {
      goLiveAt: moment(),
      featuredExpiryDate: moment(),
      ownerType: 0,
      gameExternalId: "",
      isFeatured: false,
      isNew: false,
      gameProviderCode: "",
      gameProviderStatus: "0",
      gameProviderClientId: "",
      gameProviderSecret: "",
      gameProviderUrl: "",
      tags: [],
      imagesI18n: [],
      low6GameType: 0,
    }
  );

  const [gameProviderLow6Fields, setGameProviderLow6Fields] = useState({
    gameProviderClientId: "",
    gameProviderSecret: "",
    gameProviderUrl: "",
  });

  const [marblesGames, setMarblesGames] = useState([]);
  const [low6GameTypes, setLow6GameTypes] = useState([]);
  const [generatedGameUrl, setGeneratedGameUrl] = useState("");
  const [activeLanguageTab, setActiveLanguageTab] = useState(0);

  const handleOwnerTypeChange = (event) => {
    const newOwnerType = event.target.value;

    setGameProvider((prevState) => ({
      ...prevState,
      ownerType: newOwnerType,
      gameProviderClientId:
        newOwnerType === 1 ? "" : gameProviderLow6Fields.gameProviderClientId,
      gameProviderSecret:
        newOwnerType === 1 ? "" : gameProviderLow6Fields.gameProviderSecret,
      gameProviderUrl:
        newOwnerType === 1 ? "" : gameProviderLow6Fields.gameProviderUrl,
    }));
  };

  useEffect(() => {
    if (gameProvider.ownerType === 0 || gameProvider.ownerType === 3) {
      setGameProviderLow6Fields({
        gameProviderClientId: gameProvider.gameProviderClientId,
        gameProviderSecret: gameProvider.gameProviderSecret,
        gameProviderUrl: gameProvider.gameProviderUrl,
      });
    }
  }, [
    gameProvider.ownerType,
    gameProvider.gameProviderClientId,
    gameProvider.gameProviderSecret,
    gameProvider.gameProviderUrl,
  ]);

  const [loading, setLoading] = useState(!currentGameProvider);
  const navigate = useNavigate();

  // Fetch Low6 Game Types on component load
  useEffect(() => {
    const fetchLow6GameTypes = async () => {
      try {
        const response = await get("/admin/game-providers/low6-game-types");
        setLow6GameTypes(response.low6GameTypes || []);
      } catch (error) {
        console.error("Error fetching Low6 game types:", error);
      }
    };
    const fetchMarblesGames = async () => {
      try {
        const response = await get("/admin/game-providers/marbles-games");
        setMarblesGames(response.games || []);
      } catch (error) {
        console.error("Error fetching Marbles games:", error);
      }
    };

    fetchLow6GameTypes();
    fetchMarblesGames();
  }, []);

  useEffect(() => {
    if (id === "create") {
      setGameProvider({
        goLiveAt: null,
        featuredExpiryDate: moment(),
        ownerType: 0,
        gameExternalId: "",
        isFeatured: false,
        isNew: false,
        gameProviderCode: "",
        gameProviderStatus: "0",
        gameProviderClientId: "",
        gameProviderSecret: "",
        gameProviderUrl: "",
        tags: [],
        imagesI18n: [],
        low6GameType: 0,
        nameI18n: {
          en: "",
          fr: ""
        },
        descriptionI18n: {
          en: "",
          fr: ""
        },
        transactionNameI18n: {
          en: "",
          fr: ""
        }
      });
      setLoading(false);
    } else if (id && currentGameProvider) {
      setGameProvider(currentGameProvider);
      setLoading(false);
    } else {
      setLoading(false);
      navigate(-1);
    }
  }, [id, currentGameProvider, navigate]);

  const generateGameUrl = async () => {
    setLoading(true);
    setGeneratedGameUrl(""); // Clear previous URL

    post("/admin/game-providers/marbles/generate-game-url", {
      gameId: gameProvider.gameExternalId,
      userId: 46
    })
      .then((response) => {
        if (response.url) {
          setGeneratedGameUrl(response.url);
        }
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to generate game URL. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setGameProvider((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleLanguageTabChange = (event, newValue) => {
    setActiveLanguageTab(newValue);
  };

  const handleDateChange = (newValue, fieldName) => {
    setGameProvider((prevState) => ({
      ...prevState,
      [fieldName]: moment(newValue).format(dateFormat),
    }));
  };

  const onSuccess = () => {
    const dataToSubmit = {
      gameProviderClientId: gameProvider.gameProviderClientId,
      gameProviderCode: gameProvider.gameProviderCode,
      gameProviderSecret: gameProvider.gameProviderSecret,
      gameProviderStatus: gameProvider.gameProviderStatus,
      gameProviderUrl: gameProvider.gameProviderUrl,
      goLiveAt: gameProvider.goLiveAt,
      isFeatured: gameProvider.isFeatured,
      featuredExpiryDate: gameProvider.featuredExpiryDate,
      isNew: gameProvider.isNew,
      newExpiryDate: gameProvider.newExpiryDate || null,
      imagesI18n: gameProvider.imagesI18n || [],
      tags: gameProvider.tags.length
        ? gameProvider.tags.map((item) => item.tagName)
        : [],
      ownerType: gameProvider.ownerType,
      gameExternalId: gameProvider.gameExternalId || "",
      gameProviderId: gameProvider.gameProviderId || "",
      low6GameType: gameProvider.low6GameType || 0,
      // Add multilingual data
      nameI18n: gameProvider.nameI18n,
      descriptionI18n: gameProvider.descriptionI18n,
      transactionNameI18n: gameProvider.transactionNameI18n,
    };

    setLoading(true);

    if (id === "create") {
      post("/admin/game-providers", dataToSubmit)
        .then(() => {
          navigate(-1);
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (id && gameProvider.gameProviderId) {
      put(`/admin/game-providers/${gameProvider.gameProviderId}`, dataToSubmit)
        .then(() => {
          props.onSuccessHandler?.();
          navigate(-1);
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleUserIdsChange = (selectedTags) => {
    setGameProvider((prevState) => ({
      ...prevState,
      tags: selectedTags.map((tag) => ({
        ...(tag.id && { tagId: tag.id }),
        tagName: tag.username || tag.tagName || tag.name,
      })),
    }));
  };
  console.log("00000 gameProvider edit:", gameProvider);
  return (
    <Box sx={{ padding: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography variant="h4" gutterBottom>
          Game Provider
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSuccess}
            disabled={loading}
          >
            Save
          </Button>
        </Box>
      </Stack>

      {gameProvider === null || loading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center" }}
          style={{ padding: 20 }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Basic Configuration & Game Details Card */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Basic Configuration & Game Details" />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={3} md={3}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateTimePicker
                        label="Go Live At*"
                        value={gameProvider.goLiveAt}
                        onChange={(value) => handleDateChange(value, "goLiveAt")}
                        renderInput={(params) => <TextField fullWidth {...params} />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="featured-label">Featured</InputLabel>
                      <Select
                        labelId="featured-label"
                        value={gameProvider.isFeatured}
                        label="Featured"
                        onChange={(event) =>
                          setGameProvider((prevState) => ({
                            ...prevState,
                            isFeatured: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="owner-type-label">Game Provider</InputLabel>
                      <Select
                        labelId="owner-type-label"
                        value={gameProvider.ownerType}
                        label="Game Provider"
                        onChange={handleOwnerTypeChange}
                      >
                        <MenuItem value={0}>Low6</MenuItem>
                        <MenuItem value={1}>Marbles</MenuItem>
                        <MenuItem value={3}>Rivalz</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {gameProvider.ownerType === 0 && (
                    <Grid item xs={12} sm={3} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="low6-game-type-label">Low6 Game Type</InputLabel>
                        <Select
                          labelId="low6-game-type-label"
                          value={gameProvider.low6GameType || 0}
                          label="Low6 Game Type"
                          onChange={(event) =>
                            setGameProvider((prevState) => ({
                              ...prevState,
                              low6GameType: event.target.value,
                            }))
                          }
                        >
                          {low6GameTypes.map((gameType) => (
                            <MenuItem key={gameType.value} value={gameType.value}>
                              {gameType.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {gameProvider.isFeatured && (
                    <Grid item xs={12} sm={3} md={3}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          label="Featured Expiry Date"
                          value={gameProvider.featuredExpiryDate}
                          onChange={(value) =>
                            handleDateChange(value, "featuredExpiryDate")
                          }
                          renderInput={(params) => <TextField fullWidth {...params} />}
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={3} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="new-label">New</InputLabel>
                      <Select
                        labelId="new-label"
                        value={gameProvider.isNew}
                        label="New"
                        onChange={(event) =>
                          setGameProvider((prevState) => ({
                            ...prevState,
                            isNew: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {gameProvider.isNew && (
                    <Grid item xs={12} sm={3} md={3}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          label="New Expiry Date"
                          value={gameProvider.newExpiryDate}
                          onChange={(value) => handleDateChange(value, "newExpiryDate")}
                          renderInput={(params) => <TextField fullWidth {...params} />}
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={3}>
                    <TextField
                      id="gameProviderCode"
                      label="Game Code"
                      fullWidth
                      value={gameProvider.gameProviderCode}
                      variant="outlined"
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="status-label">Status</InputLabel>
                      <Select
                        labelId="status-label"
                        value={gameProvider.gameProviderStatus}
                        label="Status"
                        onChange={(event) =>
                          setGameProvider((prevState) => ({
                            ...prevState,
                            gameProviderStatus: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="0">DRAFT</MenuItem>
                        <MenuItem value="1">ACTIVE</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Language Tabs */}
                  <Grid item xs={12}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs
                        value={activeLanguageTab}
                        onChange={handleLanguageTabChange}
                        aria-label="language tabs"
                      >
                        {langs.map((lang, index) => (
                          <Tab
                            key={lang.key}
                            label={lang.label}
                            id={`language-tab-${index}`}
                            aria-controls={`language-tabpanel-${index}`}
                          />
                        ))}
                      </Tabs>
                    </Box>
                    {langs.map((lang, index) => (
                      <TabPanel key={lang.key} value={activeLanguageTab} index={index}>
                        <GameProviderMultilingualInputs
                          gameProvider={gameProvider}
                          languageKey={lang.key}
                          languageName={lang.label}
                          onChange={setGameProvider}
                        />
                      </TabPanel>
                    ))}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Configuration Card */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Configuration" />
              <CardContent>
                <Grid container spacing={3}>
                  {gameProvider.ownerType === 0 || gameProvider.ownerType === 3 ? (
                    <>
                      {id !== "create" && (
                        <Grid item xs={6}>
                          <TextField
                            id="gameProviderClientId"
                            label="Client ID"
                            fullWidth
                            value={gameProvider.gameProviderClientId}
                            variant="outlined"
                            onChange={handleChange}
                          />
                        </Grid>
                      )}

                      {id !== "create" && (
                        <Grid item xs={6}>
                          <TextField
                            id="gameProviderSecret"
                            label="Client Secret"
                            fullWidth
                            value={gameProvider.gameProviderSecret}
                            variant="outlined"
                            onChange={handleChange}
                          />
                        </Grid>
                      )}

                      <Grid item xs={6}>
                        <TextField
                          id="gameProviderUrl"
                          label="Game URL"
                          fullWidth
                          value={gameProvider.gameProviderUrl}
                          variant="outlined"
                          onChange={handleChange}
                        />
                      </Grid>
                      {gameProvider.ownerType === 0 && (
                        <Grid item xs={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="low6-game-type-config-label">Low6 Game Type</InputLabel>
                            <Select
                              labelId="low6-game-type-config-label"
                              value={gameProvider.low6GameType || 0}
                              label="Low6 Game Type"
                              onChange={(event) =>
                                setGameProvider((prevState) => ({
                                  ...prevState,
                                  low6GameType: event.target.value,
                                }))
                              }
                            >
                              {low6GameTypes.map((gameType) => (
                                <MenuItem key={gameType.value} value={gameType.value}>
                                  {gameType.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <>
                      {gameProvider.ownerType === 1 && (
                        <>
                          <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="flex-end">
                              <Grid item xs={9}>
                                <FormControl fullWidth size="small">
                                  <InputLabel id="marbles-game-label">Marbles Game</InputLabel>
                                  <Select
                                    labelId="marbles-game-label"
                                    value={gameProvider.gameExternalId || ""}
                                    label="Marbles Game"
                                    onChange={(event) =>
                                      setGameProvider((prevState) => ({
                                        ...prevState,
                                        gameExternalId: event.target.value,
                                      }))
                                    }
                                  >
                                    <MenuItem value="">
                                      <em>Select a game</em>
                                    </MenuItem>
                                    {marblesGames.map((game) => (
                                      <MenuItem key={game.id} value={game.id}>
                                        {game.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={3}>
                                <Button
                                  variant="contained"
                                  onClick={generateGameUrl}
                                  disabled={!gameProvider.gameExternalId || loading}
                                  fullWidth
                                  size="small"
                                >
                                  Generate URL
                                </Button>
                              </Grid>
                            </Grid>
                          </Grid>
                          {generatedGameUrl && (
                            <Grid item xs={12}>
                              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Generated Game URL:
                                </Typography>
                                <TextField
                                  fullWidth
                                  value={generatedGameUrl}
                                  variant="outlined"
                                  size="small"
                                  multiline
                                  rows={2}
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  sx={{ mb: 1 }}
                                />
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(generatedGameUrl);
                                    alert("URL copied to clipboard!");
                                  }}
                                >
                                  Copy URL
                                </Button>
                              </Box>
                            </Grid>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Images Card */}
          {id !== "create" && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Images" />
                <CardContent>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      value={activeLanguageTab}
                      onChange={handleLanguageTabChange}
                      aria-label="language tabs"
                    >
                      {langs.map((lang, index) => (
                        <Tab
                          key={lang.key}
                          label={lang.label}
                          id={`language-tab-${index}`}
                          aria-controls={`language-tabpanel-${index}`}
                        />
                      ))}
                    </Tabs>
                  </Box>
                  {langs.map((lang, index) => (
                    <TabPanel key={lang.key} value={activeLanguageTab} index={index}>
                      <GameProviderImages
                        gameProvider={gameProvider}
                        onChange={setGameProvider}
                        language={lang.key}
                      />
                    </TabPanel>
                  ))}

                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Tags Card */}
          {
            id !== "create" && (
              <Grid item xs={12}>
                <Card sx={{ minHeight: 300 }}>
                  <CardHeader title="Tags" />
                  <CardContent sx={{ overflow: 'visible', position: 'relative' }}>
                    <GameTagSearch
                      onUserSelect={handleUserIdsChange}
                      users={gameProvider.tags}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )
          }

        </Grid>
      )}
    </Box>
  );
}
